import type { ReactNode } from "react";
import Typography from "@mui/material/Typography";
import { Translate, useTranslation } from "@privacy/translations";
import { Strong } from "../shared/Strong";
import { useForm, useWatch } from "react-hook-form";
import * as FormDialog from "../shared/FormDialog";
import { useCurrentCustomer } from "../root/CurrentOrganisationProvider";
import { DecisionNotice } from "./DecisionNotice";

type FormValues = { comment: string };

type Preview = { reason: "removedFromOrganisation" | "withdrawnFromLibrary"; recipient: string };

export const RemoveSharedProcessingDialog = ({
  title,
  body,
  confirmLabel,
  onConfirm,
  isPending,
  isError,
  errorMessage,
  onClose,
  preview,
}: {
  title: string;
  confirmLabel: string;
  onConfirm: (comment: string) => void;
  isPending: boolean;
  isError: boolean;
  errorMessage?: ReactNode;
  onClose: () => void;
} & ({ body: ReactNode; preview?: Preview } | { preview: Preview; body?: ReactNode })) => {
  const { t } = useTranslation();
  const currentCustomer = useCurrentCustomer();
  const { handleSubmit, control, setFocus } = useForm<FormValues>({ defaultValues: { comment: "" } });
  const comment = useWatch({ control, name: "comment" });

  FormDialog.useAutoFocusField(setFocus, "comment");

  const commentField = (
    <FormDialog.Fields sx={{ pb: preview ? 2.5 : 0 }}>
      <FormDialog.TextField
        control={control}
        name="comment"
        label={
          preview
            ? t("SharedProcessing.Remove.MessageLabel", { recipient: preview.recipient })
            : t("SharedProcessing.Remove.CommentLabel")
        }
        placeholder={t("SharedProcessing.Remove.CommentPlaceholder")}
        multiline
        minRows={2}
      />
    </FormDialog.Fields>
  );

  return (
    <FormDialog.Dialog isOpen>
      <FormDialog.Title>{title}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit(({ comment }) => onConfirm(comment))}>
        <FormDialog.Content>
          <FormDialog.FormError isError={isError} errorMessage={errorMessage} />
          {body && <Intro>{body}</Intro>}
          {preview ? (
            <>
              {commentField}
              <Intro>{t("SharedProcessing.Remove.PreviewIntro", { recipient: preview.recipient })}</Intro>
              <DecisionNotice
                tone="warning"
                eyebrow={
                  <Translate
                    translationKey={`RegistrationGrid.Drawer.OwnCopyWarning.What.${preview.reason}`}
                    values={{ customer: <Strong>{currentCustomer.name}</Strong> }}
                  />
                }
                question={t("RegistrationGrid.Drawer.OwnCopyWarning.Question")}
                quote={comment.trim() || null}
              />
            </>
          ) : (
            commentField
          )}
        </FormDialog.Content>
        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={isPending}>{confirmLabel}</FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};

const Intro = ({ children }: { children: ReactNode }) => (
  <Typography
    sx={{
      pb: 2,
      fontSize: theme => theme.typography.pxToRem(14),
      color: theme => theme.palette.text.secondary,
    }}
  >
    {children}
  </Typography>
);
